<template>
  <div class="scheme-report-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Icon & Title -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#9a6b27" size="24">mdi-wallet-membership</v-icon>
          </div>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <span class="page-title">GOLD SCHEME DEPOSIT &amp; PASSBOOK REPORT</span>
              <span class="badge-scheme-count">
                {{ filteredReportRows.length }} Entries
              </span>
            </div>
            <div class="page-subtitle">
              Customer installment collections, cumulative savings weight, recurring balances &amp; interest accruals
            </div>
          </div>
        </div>

        <!-- Right: Actions (Refresh, CSV, Print, PDF) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-btn
            icon
            small
            title="Refresh Report"
            class="refresh-icon-btn"
            :loading="appLoading"
            @click="getList"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            outlined
            color="#475569"
            class="btn-secondary text-capitalize"
            @click="exportToCSV"
            :disabled="filteredReportRows.length === 0"
          >
            <v-icon left small color="#475569">mdi-file-delimited-outline</v-icon>
            Export CSV
          </v-btn>

          <v-btn
            outlined
            color="#9a6b27"
            class="btn-gold-outline text-capitalize"
            @click="printReport"
          >
            <v-icon left small color="#9a6b27">mdi-printer-outline</v-icon>
            Print
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            :loading="pdfLoading"
            @click="downloadPDF"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Installment Collections -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL INSTALLMENTS COLLECTED</div>
          <div class="kpi-value text-emerald">{{ formatCurrency(computedTotals.installmentAmount) }}</div>
          <div class="kpi-hint">
            <span>{{ filteredReportRows.length }} payment transactions</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #15803d;">
          <v-icon size="22" color="#15803d">mdi-cash-plus</v-icon>
        </div>
      </div>

      <!-- 2. Gold Weight Credited -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">GOLD WEIGHT CREDITED</div>
          <div class="kpi-value gold-val">{{ computedTotals.weight.toFixed(3) }} <span class="unit-text">gm</span></div>
          <div class="kpi-hint">
            <span>Gold grams booked for selected period</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #b45309;">
          <v-icon size="22" color="#b45309">mdi-scale</v-icon>
        </div>
      </div>

      <!-- 3. Cumulative Scheme Weight -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">CUMULATIVE SCHEME HOLDING</div>
          <div class="kpi-value text-slate-800">{{ computedTotals.totalWeight.toFixed(3) }} <span class="unit-text">gm</span></div>
          <div class="kpi-hint">
            <span>Cumulative customer passbook weight</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="22" color="#2563eb">mdi-piggy-bank-outline</v-icon>
        </div>
      </div>

      <!-- 4. Total Interest / Bonus Accrued -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL BONUS / INTEREST</div>
          <div class="kpi-value text-amber">{{ formatCurrency(computedTotals.interestAmount) }}</div>
          <div class="kpi-hint">
            <span>Total interest credited to schemes</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #faf5ff; color: #7c3aed;">
          <v-icon size="22" color="#7c3aed">mdi-gift-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Filter & Search Toolbar Card -->
    <v-card flat class="filter-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
        <!-- Left: Search Box -->
        <div class="search-input-wrap">
          <v-text-field
            v-model="searchKeyword"
            background-color="#f8fafc"
            color="#9a6b27"
            clearable
            hide-details
            placeholder="Search by Customer Name or Scheme No..."
            outlined
            dense
            class="custom-search-field"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>

        <!-- Middle: Date Quick Presets -->
        <div class="date-presets-wrap d-flex align-center flex-wrap" style="gap: 6px;">
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'today' ? 'preset-active' : '']"
            @click="setDatePreset('today')"
          >
            Today
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'week' ? 'preset-active' : '']"
            @click="setDatePreset('week')"
          >
            This Week
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'month' ? 'preset-active' : '']"
            @click="setDatePreset('month')"
          >
            This Month
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'year' ? 'preset-active' : '']"
            @click="setDatePreset('year')"
          >
            This Year
          </v-btn>
        </div>

        <!-- Date Range Pickers (From / To) -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <v-menu
            v-model="fromMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="from"
                label="From Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="from"
              no-title
              scrollable
              color="#9a6b27"
              @input="fromMenu = false"
            ></v-date-picker>
          </v-menu>

          <span class="text-muted" style="font-size: 12px;">to</span>

          <v-menu
            v-model="toMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="to"
                label="To Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="to"
              no-title
              scrollable
              color="#9a6b27"
              @input="toMenu = false"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- Reset Button -->
        <v-btn
          v-if="searchKeyword"
          small
          text
          color="#dc2626"
          class="text-capitalize px-2"
          @click="searchKeyword = ''"
        >
          <v-icon x-small left>mdi-filter-off</v-icon>
          Reset
        </v-btn>
      </div>
    </v-card>

    <!-- Main Data Table Card -->
    <v-card flat class="table-card rounded-lg overflow-hidden">
      <!-- Table Header Bar -->
      <div class="table-header-bar px-4 py-3 d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="table-section-title">Gold Scheme Passbook Entries</span>
          <span class="table-count-badge">{{ paginatedRows.length }} of {{ filteredReportRows.length }}</span>
        </div>
        <div class="d-flex align-center" style="gap: 12px;">
          <span class="text-caption text-muted">Rows per page:</span>
          <v-select
            v-model="itemsPerPage"
            :items="[10, 20, 50, 100]"
            dense
            hide-details
            outlined
            class="rows-select"
          ></v-select>
        </div>
      </div>

      <!-- Vuetify Data Table -->
      <v-data-table
        :headers="headers"
        :items="paginatedRows"
        :loading="appLoading"
        hide-default-footer
        disable-pagination
        class="pro-scheme-table elevation-0"
      >
        <!-- Date -->
        <template v-slot:item.date="{ item }">
          <span class="date-text">{{ item.date }}</span>
        </template>

        <!-- Customer Name -->
        <template v-slot:item.customerName="{ item }">
          <div class="d-flex align-center py-1">
            <div class="customer-avatar mr-2">
              {{ getInitials(item.customerName) }}
            </div>
            <span class="customer-name font-weight-bold">{{ item.customerName || '-' }}</span>
          </div>
        </template>

        <!-- Scheme Number -->
        <template v-slot:item.schemeNo="{ item }">
          <span class="scheme-badge font-weight-bold">
            <v-icon x-small color="#1d4ed8" class="mr-1">mdi-ticket-confirmation-outline</v-icon>
            {{ item.schemeNo || '-' }}
          </span>
        </template>

        <!-- Installment Amount -->
        <template v-slot:item.installmentAmount="{ item }">
          <span class="font-weight-bold text-emerald">
            {{ formatCurrency(item.installmentAmount) }}
          </span>
        </template>

        <!-- Recurring Cumulative Amount -->
        <template v-slot:item.recurringAmount="{ item }">
          <span class="text-slate-800 font-weight-medium">
            {{ formatCurrency(item.recurringAmount) }}
          </span>
        </template>

        <!-- Weight -->
        <template v-slot:item.weight="{ item }">
          <span class="weight-chip font-weight-medium">
            {{ Number(item.weight || 0).toFixed(3) }} g
          </span>
        </template>

        <!-- Cumulative Total Weight -->
        <template v-slot:item.totalWeight="{ item }">
          <span class="weight-chip gold-chip font-weight-bold">
            {{ Number(item.totalWeight || 0).toFixed(3) }} g
          </span>
        </template>

        <!-- Interest Amount -->
        <template v-slot:item.interestAmount="{ item }">
          <span class="text-amber font-weight-medium">
            {{ formatCurrency(item.interestAmount) }}
          </span>
        </template>

        <!-- Total Interest Amount -->
        <template v-slot:item.totalInterestAmount="{ item }">
          <span class="text-slate-800">
            {{ formatCurrency(item.totalInterestAmount) }}
          </span>
        </template>

        <!-- Summary Totals Row -->
        <template v-slot:body.append>
          <tr class="table-total-row">
            <td class="text-left font-weight-bold" colspan="3">
              <div class="d-flex align-center" style="gap: 6px;">
                <v-icon small color="#9a6b27">mdi-sigma</v-icon>
                <span>SUMMARY TOTALS</span>
              </div>
            </td>
            <td class="text-right font-weight-bold text-emerald-total">{{ formatCurrency(computedTotals.installmentAmount) }}</td>
            <td class="text-right font-weight-bold text-slate-800">{{ formatCurrency(computedTotals.recurringAmount) }}</td>
            <td class="text-right font-weight-bold text-gold-total">{{ computedTotals.weight.toFixed(3) }} g</td>
            <td class="text-right font-weight-bold text-gold-total">{{ computedTotals.totalWeight.toFixed(3) }} g</td>
            <td class="text-right font-weight-bold text-amber-total">{{ formatCurrency(computedTotals.interestAmount) }}</td>
            <td class="text-right font-weight-bold text-slate-800">{{ formatCurrency(computedTotals.totalInterestAmount) }}</td>
          </tr>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="empty-table-state py-8 text-center">
            <div class="empty-icon-circle mb-3">
              <v-icon size="32" color="#94a3b8">mdi-wallet-outline</v-icon>
            </div>
            <div class="empty-title">No Gold Scheme Transactions Found</div>
            <div class="empty-subtitle">
              {{ searchKeyword ? 'No records match your search keyword. Try clearing filters.' : 'No gold scheme installment payments recorded for this date range.' }}
            </div>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination Bar -->
      <div class="table-pagination-bar px-4 py-3 d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="pagination-info">
          Showing <b>{{ filteredReportRows.length > 0 ? paginatedStart + 1 : 0 }}</b> - <b>{{ paginatedEnd }}</b> of <b>{{ filteredReportRows.length }}</b> entries
        </div>
        <v-pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
          color="#9a6b27"
          class="custom-pagination"
        ></v-pagination>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "GoldSchemeReport",
  data() {
    const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10);

    return {
      appLoading: false,
      pdfLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,

      fromMenu: false,
      toMenu: false,
      from: today,
      to: today,
      datePreset: "today",

      searchKeyword: "",
      currentPage: 1,
      itemsPerPage: 20,

      reportRows: [],
      totals: {
        installmentAmount: 0,
        recurringAmount: 0,
        weight: 0,
        totalWeight: 0,
        interestAmount: 0,
        totalInterestAmount: 0,
      },

      headers: [
        { text: "DATE", value: "date", sortable: true, align: "left", width: "120px" },
        { text: "CUSTOMER NAME", value: "customerName", sortable: true, align: "left" },
        { text: "SCHEME NO.", value: "schemeNo", sortable: true, align: "left", width: "140px" },
        { text: "INSTALLMENT (₹)", value: "installmentAmount", sortable: true, align: "right", width: "150px" },
        { text: "CUMULATIVE (₹)", value: "recurringAmount", sortable: true, align: "right", width: "150px" },
        { text: "WEIGHT (g)", value: "weight", sortable: true, align: "right", width: "130px" },
        { text: "TOTAL WT (g)", value: "totalWeight", sortable: true, align: "right", width: "130px" },
        { text: "BONUS (₹)", value: "interestAmount", sortable: true, align: "right", width: "130px" },
        { text: "TOTAL BONUS (₹)", value: "totalInterestAmount", sortable: true, align: "right", width: "140px" },
      ],
    };
  },
  computed: {
    filteredReportRows() {
      if (!this.searchKeyword || this.searchKeyword.trim() === "") {
        return this.reportRows || [];
      }
      const q = this.searchKeyword.toLowerCase().trim();
      return (this.reportRows || []).filter((r) => {
        const name = String(r.customerName || "").toLowerCase();
        const scheme = String(r.schemeNo || "").toLowerCase();
        const date = String(r.date || "").toLowerCase();
        return name.includes(q) || scheme.includes(q) || date.includes(q);
      });
    },

    totalPages() {
      return Math.ceil(this.filteredReportRows.length / this.itemsPerPage) || 1;
    },
    paginatedStart() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    paginatedEnd() {
      const end = this.paginatedStart + this.itemsPerPage;
      return end > this.filteredReportRows.length ? this.filteredReportRows.length : end;
    },
    paginatedRows() {
      return this.filteredReportRows.slice(this.paginatedStart, this.paginatedEnd);
    },

    computedTotals() {
      const totals = {
        installmentAmount: 0,
        recurringAmount: 0,
        weight: 0,
        totalWeight: 0,
        interestAmount: 0,
        totalInterestAmount: 0,
      };

      // totalWeight/totalInterestAmount are each row's running cumulative
      // value for that customer's scheme (e.g. 0.167, 0.333, 0.500 ... g) --
      // summing them across every row double/triple/etc-counts the same
      // holding. The true cumulative total is just the highest (latest)
      // value reached per scheme, so group by scheme and take the max.
      const cumulativeBySchemeKey = {};
      this.filteredReportRows.forEach((r) => {
        totals.installmentAmount += Number(r.installmentAmount) || 0;
        totals.recurringAmount += Number(r.recurringAmount) || 0;
        totals.weight += Number(r.weight) || 0;
        totals.interestAmount += Number(r.interestAmount) || 0;

        const key = (r.schemeNo || "") + "|" + (r.customerName || "");
        const rowTotalWeight = Number(r.totalWeight) || 0;
        const rowTotalInterest = Number(r.totalInterestAmount) || 0;
        if (!cumulativeBySchemeKey[key] || rowTotalWeight > cumulativeBySchemeKey[key].totalWeight) {
          cumulativeBySchemeKey[key] = cumulativeBySchemeKey[key] || {};
          cumulativeBySchemeKey[key].totalWeight = rowTotalWeight;
        }
        if (!cumulativeBySchemeKey[key] || rowTotalInterest > (cumulativeBySchemeKey[key].totalInterestAmount || 0)) {
          cumulativeBySchemeKey[key].totalInterestAmount = rowTotalInterest;
        }
      });
      Object.values(cumulativeBySchemeKey).forEach((c) => {
        totals.totalWeight += c.totalWeight || 0;
        totals.totalInterestAmount += c.totalInterestAmount || 0;
      });

      return totals;
    },
  },
  watch: {
    from() {
      this.currentPage = 1;
      this.getList();
    },
    to() {
      this.currentPage = 1;
      this.getList();
    },
    searchKeyword() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    formatCurrency(val) {
      const num = Number(val) || 0;
      return "₹ " + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    getInitials(name) {
      if (!name) return "CS";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },

    setDatePreset(preset) {
      this.datePreset = preset;
      const now = new Date();
      const formatDateISO = (d) => {
        return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().substr(0, 10);
      };

      if (preset === "today") {
        this.from = formatDateISO(now);
        this.to = formatDateISO(now);
      } else if (preset === "week") {
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        this.from = formatDateISO(start);
        this.to = formatDateISO(now);
      } else if (preset === "month") {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        this.from = formatDateISO(start);
        this.to = formatDateISO(now);
      } else if (preset === "year") {
        const start = new Date(now.getFullYear(), 0, 1);
        this.from = formatDateISO(start);
        this.to = formatDateISO(now);
      }
    },

    getList() {
      if (!this.from || !this.to) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/goldschemereport",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          from: this.from,
          to: this.to,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.reportRows = response.data.data.reportRows || [];
            this.totals = response.data.data.totals || this.totals;
          } else {
            this.msg = response.data.msg || "Failed to load report";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error loading gold scheme report:", err);
        });
    },

    printReport() {
      window.print();
    },

    downloadPDF() {
      if (!this.from || !this.to) {
        this.msg = "Please choose both dates";
        this.showsnackbar = true;
        return;
      }
      this.pdfLoading = true;
      axios({
        method: "get",
        url: "/download/goldschemereport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          from: this.from,
          to: this.to,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.pdfLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `GoldSchemeReport_${this.from}_to_${this.to}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.pdfLoading = false;
          this.ServerError = true;
          console.error("Error downloading PDF report:", err);
          this.msg = "Failed to download PDF report";
          this.showsnackbar = true;
        });
    },

    exportToCSV() {
      const rows = this.filteredReportRows;
      if (!rows || rows.length === 0) return;

      const headers = [
        "Date",
        "Customer Name",
        "Scheme Number",
        "Installment Amount (INR)",
        "Recurring Balance (INR)",
        "Weight Credited (g)",
        "Cumulative Weight (g)",
        "Bonus Amount (INR)",
        "Total Bonus (INR)"
      ];

      const csvRows = rows.map((r) => [
        `"${r.date}"`,
        `"${r.customerName}"`,
        `"${r.schemeNo}"`,
        Number(r.installmentAmount || 0).toFixed(2),
        Number(r.recurringAmount || 0).toFixed(2),
        Number(r.weight || 0).toFixed(3),
        Number(r.totalWeight || 0).toFixed(3),
        Number(r.interestAmount || 0).toFixed(2),
        Number(r.totalInterestAmount || 0).toFixed(2),
      ]);

      const totals = this.computedTotals;
      csvRows.push([
        `"TOTAL"`,
        `"--"`,
        `"--"`,
        totals.installmentAmount.toFixed(2),
        totals.recurringAmount.toFixed(2),
        totals.weight.toFixed(3),
        totals.totalWeight.toFixed(3),
        totals.interestAmount.toFixed(2),
        totals.totalInterestAmount.toFixed(2),
      ]);

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...csvRows.map(r => r.join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Gold_Scheme_Report_${this.from}_to_${this.to}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.scheme-report-wrapper {
  padding: 8px 12px;
  background-color: transparent;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fde047;
}

.page-title {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #0f172a;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.badge-scheme-count {
  font-size: 11px;
  font-weight: 700;
  background: #fefce8;
  color: #b45309;
  border: 1px solid #fef08a;
  border-radius: 6px;
  padding: 2px 8px;
}

.refresh-icon-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
}

.btn-secondary {
  border: 1px solid #cbd5e1 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-gold-outline {
  border: 1px solid #9a6b27 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-primary {
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 4px 0 2px;
}

.kpi-value.gold-val {
  color: #9a6b27;
}

.unit-text {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11.5px;
  color: #64748b;
  display: flex;
  align-items: center;
}

.kpi-icon-pill {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Filter Card */
.filter-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;
}

.search-input-wrap {
  flex: 1;
  min-width: 260px;
  max-width: 380px;
}

.custom-search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.custom-search-field :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.custom-search-field :deep(input) {
  font-size: 13px !important;
}

.preset-btn {
  font-size: 11.5px !important;
  font-weight: 600 !important;
  text-transform: capitalize !important;
  border-radius: 6px !important;
  border: 1px solid #e2e8f0 !important;
  background: #f8fafc !important;
  color: #475569 !important;
}

.preset-active {
  background: #9a6b27 !important;
  color: #ffffff !important;
  border-color: #9a6b27 !important;
}

.date-input-field {
  width: 140px;
}

.date-input-field :deep(input) {
  font-size: 12px !important;
}

/* Table Card */
.table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.table-header-bar {
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.table-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-count-badge {
  font-size: 11px;
  font-weight: 700;
  background: #e2e8f0;
  color: #334155;
  padding: 1px 6px;
  border-radius: 4px;
}

.rows-select {
  width: 75px;
}

.rows-select :deep(input) {
  font-size: 12px !important;
}

/* Table Styling */
.pro-scheme-table :deep(th) {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.pro-scheme-table :deep(td) {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 10px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.pro-scheme-table :deep(tr:hover) {
  background-color: #fafbfc !important;
}

.date-text {
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
}

.customer-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #334155;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
}

.customer-name {
  font-size: 13px;
  color: #0f172a;
}

.scheme-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.weight-chip {
  font-size: 12px;
  background: #f1f5f9;
  color: #475569;
  padding: 2px 7px;
  border-radius: 4px;
  display: inline-block;
}

.gold-chip {
  background: #fefce8;
  color: #92400e;
  border: 1px solid #fef08a;
}

/* Summary Row */
.table-total-row td {
  background: #f8fafc !important;
  border-top: 2px solid #cbd5e1 !important;
  border-bottom: none !important;
  font-size: 13px !important;
}

.text-gold-total {
  color: #9a6b27 !important;
  font-size: 13.5px !important;
}

.text-emerald-total {
  color: #059669 !important;
  font-size: 13.5px !important;
}

.text-amber-total {
  color: #b45309 !important;
  font-size: 13.5px !important;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Empty State */
.empty-table-state {
  padding: 40px 20px;
}

.empty-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* Utilities */
.text-emerald {
  color: #059669;
}

.text-amber {
  color: #b45309;
}

.text-slate-800 {
  color: #1e293b;
}

.text-muted {
  color: #94a3b8;
}
</style>
